import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getFxRates } from '../services/api';

/**
 * Currency context — holds the user's chosen display currency + FX rates.
 *
 * Usage:
 *   const { currency, setCurrency, format, convert } = useCurrency();
 *   format(1234.56)  → "$1,234.56" or "€1,150.32" depending on selection
 *   convert(100)     → 100 in the chosen currency (USD passthrough if USD)
 *
 * Persists choice in localStorage. Refreshes rates once per hour (matches
 * backend cache TTL — Frankfurter only updates once per business day).
 */

const CurrencyContext = createContext({
  currency: 'USD',
  setCurrency: () => {},
  rates: { USD: 1 },
  supportedCurrencies: ['USD'],
  format: (n) => `$${n.toFixed(2)}`,
  convert: (n) => n,
  loading: false,
});

const SYMBOL = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', INR: '₹',
  BRL: 'R$', AUD: 'A$', CAD: 'C$', CHF: 'CHF', KRW: '₩',
  MXN: 'MX$', SGD: 'S$', HKD: 'HK$', TRY: '₺', ZAR: 'R',
};

const STORAGE_KEY = 'haythix.displayCurrency';

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'USD'; } catch { return 'USD'; }
  });
  const [rates, setRates] = useState({ USD: 1 });
  const [supportedCurrencies, setSupportedCurrencies] = useState(['USD', 'EUR', 'GBP', 'JPY']);
  const [loading, setLoading] = useState(false);

  const refreshRates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFxRates('USD');
      const d = res?.data || res;
      const next = { USD: 1, ...(d.rates || {}) };
      setRates(next);
      // Mirror into the displayPrefs singleton so non-React formatters
      // (e.g. api.js's formatUsd) get currency-aware too.
      import('../services/displayPrefs').then(m => m.setRates(next)).catch(() => {});
      if (Array.isArray(d.supportedCurrencies)) {
        setSupportedCurrencies(['USD', ...d.supportedCurrencies]);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refreshRates();
    const interval = setInterval(refreshRates, 60 * 60 * 1000); // 1 hour
    return () => clearInterval(interval);
  }, [refreshRates]);

  function setCurrency(c) {
    setCurrencyState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch {}
    // Broadcast so the displayPrefs singleton + any other listeners
    // pick the change up immediately (without waiting for a reload).
    try { window.dispatchEvent(new CustomEvent('haythix:currency', { detail: c })); } catch {}
  }

  function convert(usdAmount) {
    if (usdAmount === null || usdAmount === undefined) return null;
    const n = Number(usdAmount);
    if (!Number.isFinite(n)) return null;
    const rate = rates[currency] || 1;
    return n * rate;
  }

  function format(usdAmount, dp = 2) {
    if (usdAmount === null || usdAmount === undefined) return '—';
    const converted = convert(usdAmount);
    if (converted === null) return '—';
    const sym = SYMBOL[currency] || `${currency} `;
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    });
    return `${sym}${formatted}`;
  }

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, supportedCurrencies, format, convert, loading, refreshRates }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
